import { isStaleAssetMessage } from './deployVersionSync';

/**
 * Minimal shape of a PostHog `$exception` event. Typed loosely so we do not
 * depend on posthog-js internal types, which are not part of its public API.
 */
interface ExceptionFrame {
  filename?: string;
  module?: string;
  function?: string;
}

interface ExceptionValue {
  type?: string;
  value?: string;
  stacktrace?: { frames?: ExceptionFrame[] };
}

interface CapturedEvent {
  properties?: { $exception_list?: ExceptionValue[] };
}

/**
 * Faults thrown inside a third-party session recorder while it walks the DOM,
 * never by our own code. Firefox raises a security-wrapper error when the rrweb
 * mutation observer reads a node it is not allowed to touch (for example a
 * cross-origin iframe), so the message is stable across builds.
 */
const THIRD_PARTY_RECORDER_PATTERNS: RegExp[] = [/Permission denied to access property/i];

/** Bundle markers for third-party observability packages (Sentry, rrweb, PostHog). */
const THIRD_PARTY_BUNDLE_MARKERS: RegExp[] = [/node_modules/i, /@sentry/i, /rrweb/i, /posthog-js/i];

function isThirdPartyFrame(frame: ExceptionFrame): boolean {
  const haystack = `${frame.filename ?? ''} ${frame.module ?? ''} ${frame.function ?? ''}`;
  return THIRD_PARTY_BUNDLE_MARKERS.some(marker => marker.test(haystack));
}

/** True when a fault comes from a third-party recorder, by message or by stack. */
function isThirdPartyRecorderException(value: ExceptionValue): boolean {
  if (THIRD_PARTY_RECORDER_PATTERNS.some(pattern => pattern.test(value.value ?? ''))) {
    return true;
  }
  const frames = value.stacktrace?.frames ?? [];
  return frames.length > 0 && frames.every(isThirdPartyFrame);
}

/** Stale hashed chunks after a deploy — recovered by a hard reload, not real bugs. */
function isStaleAssetException(value: ExceptionValue): boolean {
  return isStaleAssetMessage(value.value ?? '') || isStaleAssetMessage(value.type ?? '');
}

/**
 * True when a captured PostHog exception event should be dropped before send:
 * stale assets after a deploy, or a fault from a third-party session recorder
 * rather than our code. The whole event must be noise — a single application
 * cause keeps the event. Non-exception events are always kept.
 *
 * Shared by `posthog.init`'s `before_send` (window errors) and
 * `captureClientException` (manual reports), so both paths filter the same way.
 * Mirrors the `beforeSend` guard in `sentry.ts`.
 */
export function shouldDropExceptionEvent(event: unknown): boolean {
  if (!event || typeof event !== 'object') return false;
  const values = (event as CapturedEvent).properties?.$exception_list;
  if (!values || values.length === 0) return false;
  return values.every(
    value => isStaleAssetException(value) || isThirdPartyRecorderException(value)
  );
}
